import React from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import quantityStepperPillSvg from "assets/images/quantity-stepper-pill.svg";
import { COLORS } from "components/v3/Theme/colors";

const STEP_OUTER_WIDTH_PX = 150;
const STEP_OUTER_HEIGHT_PX = 56;
const HIT_DECREMENT_END = "32%";
const HIT_CENTER_START = "32%";
const HIT_CENTER_WIDTH = "36%";
const PILL_END_RADIUS_PX = 15;
const PILL_OUTER_RADIUS_PX = STEP_OUTER_HEIGHT_PX / 2;
const HOVER_BG = alpha(COLORS.PRIMARY_LIGHT, 0.2);
const HOVER_TRANSITION = "background-color 180ms ease-in-out";

/** Flatten `sx` for spreading; MUI’s array `SxProps` type is not spread-friendly alone. */
function extraSxParts(prop: SxProps<Theme> | undefined): SxProps<Theme>[] {
  if (prop === undefined) {
    return [];
  }
  if (Array.isArray(prop)) {
    return [...prop] as SxProps<Theme>[];
  }
  return [prop];
}

export type GLQuantityStepperProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  decrementAriaLabel?: string;
  incrementAriaLabel?: string;
  sx?: SxProps<Theme>;
};

const GLQuantityStepper: React.FC<GLQuantityStepperProps> = ({
  value,
  onChange,
  min = 1,
  max,
  decrementAriaLabel = "Decrease quantity",
  incrementAriaLabel = "Increase quantity",
  sx
}) => {
  const atMin = value <= min;
  const atMax = max !== undefined && value >= max;

  const rootSx: SxProps<Theme> = [
    {
      flexShrink: 0,
      height: STEP_OUTER_HEIGHT_PX,
      position: "relative",
      width: STEP_OUTER_WIDTH_PX,
      // Keyboard only — avoids a sharp rectangle after mouse click (focus-within).
      "&:has(:focus-visible)": {
        borderRadius: `${PILL_OUTER_RADIUS_PX}px`,
        boxShadow: `0 0 0 2px ${COLORS.PRIMARY}`
      }
    },
    ...extraSxParts(sx)
  ] as SxProps<Theme>;

  const segmentButtonSx = {
    bgcolor: "transparent",
    borderRadius: 0,
    height: "100%",
    position: "absolute" as const,
    top: 0,
    transition: HOVER_TRANSITION,
    zIndex: 0,
    "&:hover": {
      bgcolor: HOVER_BG
    },
    "&.Mui-disabled": {
      opacity: 0.4
    },
    "&:focus": {
      outline: "none"
    }
  };

  return (
    <Box sx={rootSx}>
      <ButtonBase
        aria-label={decrementAriaLabel}
        disableRipple
        disabled={atMin}
        onClick={() => onChange(Math.max(min, value - 1))}
        sx={{
          ...segmentButtonSx,
          borderBottomLeftRadius: `${PILL_END_RADIUS_PX}px`,
          borderTopLeftRadius: `${PILL_END_RADIUS_PX}px`,
          left: 0,
          width: HIT_DECREMENT_END
        }}
      />
      <ButtonBase
        aria-label={incrementAriaLabel}
        disableRipple
        disabled={atMax}
        onClick={() => {
          const next =
            max === undefined ? value + 1 : Math.min(max, value + 1);
          onChange(next);
        }}
        sx={{
          ...segmentButtonSx,
          borderBottomRightRadius: `${PILL_END_RADIUS_PX}px`,
          borderTopRightRadius: `${PILL_END_RADIUS_PX}px`,
          right: 0,
          width: HIT_DECREMENT_END
        }}
      />
      <Box
        alt=""
        aria-hidden
        component="img"
        src={quantityStepperPillSvg}
        sx={{
          display: "block",
          height: STEP_OUTER_HEIGHT_PX,
          left: 0,
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          userSelect: "none",
          width: STEP_OUTER_WIDTH_PX,
          zIndex: 1
        }}
      />
      <Box
        aria-hidden
        sx={{
          alignItems: "center",
          display: "flex",
          height: "100%",
          justifyContent: "center",
          left: HIT_CENTER_START,
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          width: HIT_CENTER_WIDTH,
          zIndex: 2
        }}
      >
        <Typography
          component="span"
          sx={{ fontVariantNumeric: "tabular-nums" }}
          variant="body_strong_black"
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

export default GLQuantityStepper;
